import React, { useEffect, useMemo, useRef } from 'react';
import { currencySymbol } from '@/utils/currencies';
import { BarChart } from '@/features/ai-agents/chat/MarkdownTableVisualization/BarChart';
import { formatChartMetricValue } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import { LineChart } from '@/features/ai-agents/chat/MarkdownTableVisualization/LineChart';
import {
  ComparePieData,
  PieChart,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/PieChart';
import { SimpleTableView } from '@/features/ai-agents/chat/MarkdownTableVisualization/SimpleTableView';
import { VisualizationControls } from '@/features/ai-agents/chat/MarkdownTableVisualization/VisualizationControls';
import {
  ChartContainer,
  Container,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import { TableView } from '@/features/ai-agents/chat/MarkdownTableVisualization/TableView';
import {
  ChartRow,
  ChartSeries,
  ChartView,
  CompareLineChartData,
  MarkdownTableVisualizationProps,
  TooltipTextResolver,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import { useChartData } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-chart-data';
import { useTableData } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-table-data';
import { useTableDragSensors } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-table-drag-sensors';
import { useVisualizationState } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-visualization-state';
import { useDefaultViewHint } from '@/features/ai-agents/chat/default-view-hint';
import { resolveTooltipText } from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

export function MarkdownTableVisualization({
  children,
  node,
  onViewChange,
}: MarkdownTableVisualizationProps) {
  const tableData = useTableData(node, children);
  const defaultViewHint = useDefaultViewHint();
  const visualization = useVisualizationState({
    chartNumericColumnIndexes: tableData.chartNumericColumnIndexes,
    compareColumnIndex: tableData.compareColumnIndex,
    defaultViewHint,
    defaultXColumnIndex: tableData.defaultXColumnIndex,
    isVariantReport: tableData.isVariantReport,
    table: tableData.table,
    variantColumnIndex: tableData.variantColumnIndex,
  });
  const chart = useChartData({
    compareColumnIndex: tableData.compareColumnIndex,
    isCompareReport: tableData.isCompareReport,
    isVariantReport: tableData.isVariantReport,
    selectedXColumnIndex: visualization.selectedXColumnIndex,
    selectedYColumnIndex: visualization.selectedYColumnIndex,
    sortedRows: visualization.sortedRows,
    variantColumnIndex: tableData.variantColumnIndex,
  });
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    onViewChange?.({
      type: visualization.activeView,
      xAxis: tableData.table.headers[visualization.selectedXColumnIndex],
      yAxis: tableData.table.headers[visualization.selectedYColumnIndex],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visualization.activeView,
    visualization.selectedXColumnIndex,
    visualization.selectedYColumnIndex,
  ]);
  const sensors = useTableDragSensors();
  const hintTooltips = defaultViewHint?.tooltips ?? null;
  const getTooltip: TooltipTextResolver = useMemo(
    () => (label: string) => resolveTooltipText(hintTooltips, label),
    [hintTooltips],
  );

  if (!visualization.canShowChart) {
    return (
      <SimpleTableView
        numericColumnIndexes={tableData.numericColumnIndexes}
        renderedTable={tableData.renderedTable}
        table={tableData.table}
      />
    );
  }

  return (
    <Container className="table-arrows-portal">
      <VisualizationControls
        activeView={visualization.activeView}
        availableChartViews={visualization.availableChartViews}
        availableXColumnIndexes={visualization.availableXColumnIndexes}
        canShowChart={visualization.canShowChart}
        chartNumericColumnIndexes={tableData.chartNumericColumnIndexes}
        getTooltip={getTooltip}
        headers={tableData.table.headers}
        onSelectView={visualization.setView}
        onSelectXColumnIndex={visualization.setXColumnIndex}
        onSelectYColumnIndex={visualization.setYColumnIndex}
        orderedColumnIndexes={visualization.orderedColumnIndexes}
        selectedXColumnIndex={visualization.selectedXColumnIndex}
        selectedYColumnIndex={visualization.selectedYColumnIndex}
      />
      {visualization.activeView === `table` ? (
        <TableView
          currentSort={visualization.currentSort}
          getTooltip={getTooltip}
          numericColumnIndexes={tableData.numericColumnIndexes}
          onColumnOrderChange={visualization.setColumnOrder}
          onSort={visualization.handleSort}
          orderedColumnIndexes={visualization.orderedColumnIndexes}
          renderedTable={tableData.renderedTable}
          sensors={sensors}
          sortedRows={visualization.sortedRows}
          table={tableData.table}
        />
      ) : (
        <DataChart
          type={visualization.activeView}
          compareGroupedData={chart.compareGroupedData}
          comparePieData={chart.comparePieData}
          data={chart.chartData}
          series={chart.variantSeries}
          valueLabel={
            tableData.table.headers[visualization.selectedYColumnIndex]
          }
        />
      )}
    </Container>
  );
}

interface DataChartProps {
  compareGroupedData?: CompareLineChartData | null;
  comparePieData?: ComparePieData | null;
  data: ChartRow[];
  series?: ChartSeries[];
  type: ChartView;
  valueLabel: string;
}

function DataChart({
  compareGroupedData,
  comparePieData,
  data,
  series = [],
  type,
  valueLabel,
}: DataChartProps) {
  const symbol = currencySymbol();
  const formatValue = (value: number | string) =>
    formatChartMetricValue(value, valueLabel, symbol);

  return (
    <ChartContainer role="img" aria-label={`${type} chart`}>
      {type === `line` && (
        <LineChart
          compareData={compareGroupedData}
          data={data}
          formatValue={formatValue}
          series={series}
          valueLabel={valueLabel}
        />
      )}
      {type === `bar` && (
        <BarChart
          compareData={compareGroupedData}
          data={data}
          formatValue={formatValue}
          valueLabel={valueLabel}
        />
      )}
      {type === `pie` && (
        <PieChart
          compareData={comparePieData}
          data={data}
          formatValue={formatValue}
          totalLabel={`Total ${valueLabel}`}
        />
      )}
    </ChartContainer>
  );
}

export type { DefaultViewChange } from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
export {
  formatChartMetricValue,
  isCurrencyMetric,
  isPercentageMetric,
  PieValueLabel,
  TruncatedXAxisTick,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
export {
  MarkdownTableBody,
  MarkdownTableCell,
  MarkdownTableHead,
  MarkdownTableHeader,
  MarkdownTableRow,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/TableView';
export {
  createVariantChartSeries,
  createVariantLineChartData,
  findVariantColumnIndex,
  formatColumnHeader,
  parseMarkdownTable,
  parseNumericValue,
  reorderColumns,
  resolveTooltipText,
  sortChartRowsByDate,
  sortTableRows,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';
